import React, { lazy, useEffect, useMemo } from 'react';
import { IBasicDetails, INodeWithPath, ITreeNode } from '../../../interface/interfaces';
import { Title } from '../../ui/Title';
import { substringWords } from '../../../utils/substringWords';
import { camelCaseToWords } from '../../../utils/camelCaseToWord';
import { TColorRole, TSize, colorMap } from '../../../types/types';
import { hexToRgb } from '../../../utils/hexToRgb';
import { ExecButton } from '../../ui/ExecButton/ExecButton';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { mappingGermanToEnglish } from '../../../utils/mapping';
import { FlexCenter } from '../../layout/FlexCenter/FlexCenter';
import IconComponent from '../../shared/IconComponents/IconComponent';
import { rootState } from '../../../redux/store';
import { useTranslation } from 'react-i18next';
import { StatusListIcons } from '../../shared/StatusListIcons/StatusListIcons';
import { useDroppable } from '@dnd-kit/core';
import { TextCustom } from '../../ui/TextCustom';

interface INodeProps {
    toolTipOn?: boolean;
    toolTipText?: string;
    nodeDetail: ITreeNode;
    conflictSize: number;
    showChildren?: boolean;
    color?: TColorRole;
    borderColor?: TColorRole;
    transparanetIntensityColor?: number;
    transparentIntensityBorder?: number;
    mergeBtn?: boolean;
    size?: TSize;
    mergeBtnIconSize?: number;
    textColor?: TColorRole;
    showIcon?: boolean;
    useChecker?: boolean;
    func?: () => void;
    hasConflictIcon?: boolean;
    overTempNodeId?:string
}

const IconLoader = lazy(() => import('../../features/IconLoader/IconLoader'));
const MemoizedIconLoader = React.memo(IconLoader);

const Node: React.FC<INodeProps> = ({
    useChecker = true,
    toolTipOn = false,
    toolTipText = '',
    showIcon = true,
    textColor = 'dark',
    mergeBtnIconSize = 2,
    size = 's',
    mergeBtn = false,
    nodeDetail,
    conflictSize,
    showChildren = true,
    color = 'grey-dark',
    borderColor = 'grey-dark',
    transparanetIntensityColor = 0.2,
    transparentIntensityBorder = 0.2,
    hasConflictIcon = true,
    overTempNodeId = '',
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    func = () => {},
}) => {
    const hexColor = colorMap[color] || colorMap['white'];
    const hexBorderColor = colorMap[borderColor] || colorMap['grey-light'];
    const hexTextColor = colorMap[textColor] || colorMap['dark'];
    const { dataAllChildrensPerNode, nodeParents } = useAppSelector((state: rootState) => state.filteredChildrenNodesPerSelection);
    const {setNodeRef} = useDroppable({id:nodeDetail.Id, data:nodeDetail});
    const { t, i18n } = useTranslation();

    // get nodes
    const getDataAllChildrensPerNodeIds = useMemo(()=>{
        return dataAllChildrensPerNode.map((item: IBasicDetails) => {
            return item.Id;
        })
    }, [dataAllChildrensPerNode, nodeParents]);

    const nodeParentsIds = useMemo(()=>{
        return nodeParents.map((item: IBasicDetails) => {
            return item.Id
        })
    }, [dataAllChildrensPerNode, nodeParents]);


    const dispatch = useAppDispatch();

    const { r, g, b } = hexToRgb(hexColor);
    const { r: rb, g: gb, b: bb } = hexToRgb(hexBorderColor);
    const { r: rt, g: gt, b: bt } = hexToRgb(hexTextColor);


    if (!nodeDetail) {
        return <div></div>;
    }

    const { Name, Id, Type, StatusList, Children, NumberOfPassengers } = nodeDetail;
    const key = `${Name}${Type}${Id}`;



    return (
        <FlexCenter direction="v">
            <div
                ref={setNodeRef}
                key={key}
                className={`node node__default`}
                style={{
                    backgroundColor: getDataAllChildrensPerNodeIds.includes(nodeDetail.Id)
                        ? `rgba(${r}, ${g}, ${b}, ${transparanetIntensityColor * 1.5})`
                        : `rgba(${r}, ${g}, ${b}, ${transparanetIntensityColor*.9})`,
                    border: `${getDataAllChildrensPerNodeIds.includes(nodeDetail.Id) ? '1px' : '1px'} solid rgba(${rb}, ${gb}, ${bb}, ${getDataAllChildrensPerNodeIds.includes(nodeDetail.Id) ? transparentIntensityBorder * 1.3 : transparentIntensityBorder})`,
                    width: '100%',
                    outline: overTempNodeId === nodeDetail.Id ? '3px solid #bdd8aa' : 'none'
                }}
            >
                <div className="node__name">
                    <IconComponent size={1.6} iconName={`${camelCaseToWords(Type)}`} />
                    <Title text={substringWords(Name, 12)} size="m" />
                    <TextCustom color={textColor} size={1.1} weight={400}>
                        {mappingGermanToEnglish(camelCaseToWords(Type))}
                    </TextCustom>
                    {/* <div className="node__avatar" style={{ color: `rgba(${rt}, ${gt}, ${bt}, 1)` }}>
                        {mappingGermanToEnglish(camelCaseToWords(Type))}
                    </div> */}
                </div>

                <div className="node__info">
                    {/* CHANGE TO LIST */}
                    <StatusListIcons statusList={StatusList} iconSize={1.4}/>

                    {/* CHANGE TO PASSENGERS */}
                    {/* {showChildren && <div className="node__childrens">{NumberOfPassengers}</div>} */}
                    {/* {showChildren && <div className="node__childrens">{Children?.length}</div>} */}
                    {showChildren && <div className="node__childrens">{NumberOfPassengers}</div>}

                    {mergeBtn && <ExecButton showIcon={showIcon} textColor={textColor} iconSize={mergeBtnIconSize} size={size} label={'Merge'} iconName={'Merge'} func={func} />}
                </div>
            </div>
            {/* Selected children nodes */}
            {
                getDataAllChildrensPerNodeIds.includes(nodeDetail.Id) && useChecker && 
                <IconComponent size={1.6} iconName={'Selected_tree_icon'} />
            }

            {/* Parent nodes from selected children nodes */}
            {
                nodeParentsIds.includes(nodeDetail.Id) && useChecker && 
                <IconComponent size={1.6} iconName={'Half_selected_tree_icon'} />
            }

        </FlexCenter>
    );
};

const MemoizedNode = React.memo(Node);
MemoizedNode.displayName = 'Node';

export default MemoizedNode;
